import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import dayjs from "dayjs";
import { UNIFIED_CHECKOUT_BASKET_SUCCESS } from "../../../constants/routes";
import { useCheckoutPaymentStatus } from "../../../hooks/api/v2/checkout/useCheckoutPaymentStatus";
import { useUnifiedCheckoutBasket } from "../../../hooks/api/v2/unified-checkout-basket/useUnifiedCheckoutBaskets";

const POLLING_INTERVAL_MS = 5000;
const WAIT_FOR_WINDOW_MINUTES = 10;

const RETURN_PARAM_NAMES = [
  "paymentReturn",
  "supplierId",
  "orderId",
  "status",
  "payment_intent",
  "payment_intent_client_secret",
  "redirect_status",
];

export const useUnifiedCheckoutPaymentReturn = (sdBasketUuid: string) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { refetch: refetchBasket } = useUnifiedCheckoutBasket(sdBasketUuid);
  const paymentReturn = searchParams.get("paymentReturn");
  const supplierId = Number(searchParams.get("supplierId"));
  const orderId = Number(searchParams.get("orderId"));
  const isPaymentReturn =
    (paymentReturn === "stripe" || paymentReturn === "sellar-credit") &&
    supplierId > 0 &&
    orderId > 0;
  const isCancelled =
    paymentReturn === "sellar-credit" &&
    searchParams.get("status") === "cancelled";
  const [error, setError] = useState("");

  const paymentStatusQuery = useCheckoutPaymentStatus(
    { orderId },
    {
      enabled: isPaymentReturn && !isCancelled,
      refetchInterval: (response) => {
        if (response.state.data?.status === "not_started") {
          return POLLING_INTERVAL_MS;
        }
        if (
          response.state.data?.status !== "pending" ||
          !response.state.data.paymentCreatedAt
        ) {
          return false;
        }
        return dayjs().diff(
          dayjs(response.state.data.paymentCreatedAt),
          "minutes",
        ) < WAIT_FOR_WINDOW_MINUTES
          ? POLLING_INTERVAL_MS
          : false;
      },
      refetchOnWindowFocus: false,
    },
  );

  const status = isCancelled
    ? "failed"
    : (paymentStatusQuery.data?.status ?? "pending");
  const hasSettled = status === "failed" || status === "succeeded";

  useEffect(() => {
    if (!isPaymentReturn || !hasSettled) return;

    let active = true;
    const settlePaymentReturn = async () => {
      try {
        const basket = await refetchBasket().then((result) => result.data);
        if (!active) return;

        const nextSearchParams = new URLSearchParams(searchParams);
        RETURN_PARAM_NAMES.forEach((name) => nextSearchParams.delete(name));
        setSearchParams(nextSearchParams, { replace: true });

        if (status === "succeeded" && basket?.DraftOrders.length === 0) {
          navigate(UNIFIED_CHECKOUT_BASKET_SUCCESS.href({ sdBasketUuid }), {
            replace: true,
          });
        }
      } catch {
        if (active) {
          setError(
            paymentReturn === "sellar-credit"
              ? "We couldn't confirm your Sellar Credit payment. Please try again."
              : "We couldn't confirm your card payment. Please try again.",
          );
        }
      }
    };

    settlePaymentReturn();
    return () => {
      active = false;
    };
  }, [
    isPaymentReturn,
    hasSettled,
    navigate,
    paymentReturn,
    refetchBasket,
    sdBasketUuid,
    searchParams,
    setSearchParams,
    status,
  ]);

  return {
    error,
    isValidatingPaymentReturn: isPaymentReturn && !error && !hasSettled,
  };
};
